import { useChannel } from '@services/channels';
import { useShop } from '@services/ShopProvider';

/**
 * Custom hook to check if the current shop is a Local Feed Partner
 *
 * @param {string} shopId - Optional shop ID, defaults to current shop from context
 * @returns {boolean} - True if the shop is a Local Feed Partner, false otherwise
 */
export const useIsLocalFeedPartner = (shopId = null) => {
	const { shop } = useShop();
	const targetShopId = shopId || shop?.id;

	const { data: googleChannel } = useChannel('google', targetShopId, {
		suspense: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		refreshInterval: 0,
		dedupingInterval: 300000,
		errorRetryCount: 1,
		shouldRetryOnError: false,
		fallbackData: null
	});

	return googleChannel?.config?.feedOnly === true;
};

/**
 * A wrapper component that hides its children if the shop has feedOnly enabled
 * for their Google channel configuration.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to conditionally render
 * @returns {React.ReactNode|null} - Returns children if shop is not LFP, null otherwise
 */
const HideForLocalFeedPartner = ({ children }) => {
	const isLocalFeedPartner = useIsLocalFeedPartner();

	// If Google channel config has feedOnly: true, don't render children
	if (isLocalFeedPartner) {
		return null;
	}

	return children;
};

export default HideForLocalFeedPartner;
